import type { CSSProperties } from "react";

/**
 * Sprint 14c — generic scope-change confirmation (S14C.38).
 *
 * Surfaces a `_warnings` entry from `set_node_access` (widening) or any other
 * access-scope change before it completes. Reused by ManageAccessModal when a
 * change would widen who can see content. Amber framing — this is a "are you
 * sure?" gate, not an error.
 */
export function ScopeChangeWarningModal({
  open,
  title = "Confirm scope change",
  message,
  confirmLabel = "Proceed",
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title?: string;
  message: string;
  confirmLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 110,
    fontFamily: "var(--font-sans)",
  };
  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 440,
    maxWidth: "90vw",
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <div style={modal} role="alertdialog" aria-label={title}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 15,
            fontWeight: 500,
            marginBottom: 8,
            color: "var(--color-text-primary)",
          }}
        >
          <i className="ti ti-alert-triangle" style={{ color: "var(--c-amber-600)" }} aria-hidden="true" />
          {title}
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
          {message}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} disabled={busy} style={{ fontSize: 13, padding: "8px 14px" }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              fontSize: 13,
              padding: "8px 14px",
              borderRadius: "var(--border-radius-md)",
              border: "none",
              fontWeight: 500,
              fontFamily: "var(--font-sans)",
              cursor: busy ? "default" : "pointer",
              background: "var(--c-amber-600)",
              color: "#fff",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
